import { Box, Chip, IconButton, Stack, Typography } from "@mui/material";
import {
  ChevronLeftRounded as ChevronLeftRoundedIcon,
  ChevronRightRounded as ChevronRightRoundedIcon,
} from "@mui/icons-material";
import { useEffect, useMemo, useRef, useState } from "react";
import type { BudgetRange } from "../../../__generated__/types";
import { bugetRangeLabels } from "../../../utils/option-helpers";
import { BUDGET_RANGES } from "../../../components/homeowner-preferences-quiz/constants";
import { PROJECT_SCOPE_OPTIONS } from "../../homeowner-bio-page/data";
import {
  formatBudgetRangeLabel,
  formatShortDate,
  formatValue,
  toTitleCase,
} from "../utils";
import type { HomeownerBioRecord, HomeownerProjectRecord } from "../types";

type NotesTabProps = {
  bio: HomeownerBioRecord | null;
  projects: HomeownerProjectRecord[];
};

const getOptionLabel = (options: { value: string; label: string }[], value?: string | null) => {
  if (!value) return "";
  const match = options.find((option) => option.value === value || option.label === value);
  return match?.label ?? toTitleCase(value);
};

const getMultiOptionLabel = (
  options: { value: string; label: string }[],
  values?: Array<string | null | undefined>
) => {
  if (!values?.length) return "";
  const labels = values
    .filter(Boolean)
    .map((value) => getOptionLabel(options, String(value)))
    .filter(Boolean);
  return labels.length ? labels.join(", ") : "";
};

const NotesTab = ({ bio, projects }: NotesTabProps) => {
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const projectScrollRef = useRef<HTMLDivElement | null>(null);

  const sortedProjects = useMemo(() => {
    const list = projects.filter(Boolean);
    return [...list].sort((a, b) => {
      const dateA = a.projectStartDate ? new Date(a.projectStartDate).getTime() : 0;
      const dateB = b.projectStartDate ? new Date(b.projectStartDate).getTime() : 0;
      if (dateA !== dateB) return dateB - dateA;
      return (a.projectName ?? "").localeCompare(b.projectName ?? "");
    });
  }, [projects]);

  useEffect(() => {
    if (!sortedProjects.length) {
      setSelectedProjectId(null);
      return;
    }
    if (selectedProjectId && sortedProjects.some((project) => project._id === selectedProjectId)) {
      return;
    }
    const defaultProject =
      sortedProjects.find((project) => project.active) ?? sortedProjects[0];
    setSelectedProjectId(defaultProject?._id ?? null);
  }, [selectedProjectId, sortedProjects]);

  const selectedProject = useMemo(
    () =>
      sortedProjects.find((project) => project._id === selectedProjectId) ??
      sortedProjects[0] ??
      null,
    [selectedProjectId, sortedProjects]
  );

  const getProjectBudgetLabel = (project: HomeownerProjectRecord) => {
    const budget = project.budgetRange;
    if (!budget) return "Not provided";
    const label =
      BUDGET_RANGES.find((option) => option.value === budget)?.label ??
      bugetRangeLabels[budget as BudgetRange] ??
      toTitleCase(budget);
    return label ? formatBudgetRangeLabel(label) : "Not provided";
  };

  const getProjectScopeLabel = (project: HomeownerProjectRecord) => {
    const scopeLabel = getMultiOptionLabel(
      PROJECT_SCOPE_OPTIONS,
      project.projectScope ?? undefined
    );
    if (scopeLabel) return scopeLabel;
    return formatValue("", "Not provided");
  };

  const scrollProjects = (direction: "left" | "right") => {
    const container = projectScrollRef.current;
    if (!container) return;
    const cardWidth =
      (container.firstElementChild as HTMLElement | null)?.offsetWidth ?? 240;
    const offset = direction === "left" ? -cardWidth - 16 : cardWidth + 16;
    container.scrollBy({ left: offset, behavior: "smooth" });
  };

  const wishlistItems = useMemo(() => {
    const items = selectedProject?.wishList ?? bio?.wishList ?? [];
    return items.filter(Boolean) as string[];
  }, [bio?.wishList, selectedProject?.wishList]);

  const projectGoals = useMemo(
    () => selectedProject?.notes?.trim() ?? bio?.notes?.trim() ?? "",
    [bio?.notes, selectedProject?.notes]
  );

  return (
    <Stack
      spacing={{ xs: 4, md: 8 }}
      sx={{ pt: { xs: 2, md: 3 }, pb: { xs: 6, md: "100px" }, px: { xs: 2, md: 0 }, alignItems: "center" }}
    >
      <Typography
        align="center"
        sx={{
          color: "#6B7280",
          fontSize: "0.95rem",
        }}
      >
        Additional details and key considerations.
      </Typography>

      <Box sx={{ width: "100%", px: "40px" }}>
        <Typography
          sx={{
            fontWeight: 700,
            color: "#111827",
            fontSize: "1.05rem",
          }}
        >
          Projects
        </Typography>
        {sortedProjects.length ? (
          <Box sx={{ mt: 2, position: "relative", width: "100%" }}>
            <IconButton
              aria-label="Scroll projects left"
              onClick={() => scrollProjects("left")}
              sx={{
                position: "absolute",
                left: -12,
                top: "50%",
                transform: "translateY(-50%)",
                backgroundColor: "#fff",
                border: "1px solid #E5E7EB",
                boxShadow: "0 6px 16px rgba(15, 23, 42, 0.08)",
                "&:hover": { backgroundColor: "#F9FAFB" },
                display: { xs: "none", md: "inline-flex" },
                zIndex: 1,
              }}
            >
              <ChevronLeftRoundedIcon />
            </IconButton>
            <IconButton
              aria-label="Scroll projects right"
              onClick={() => scrollProjects("right")}
              sx={{
                position: "absolute",
                right: -12,
                top: "50%",
                transform: "translateY(-50%)",
                backgroundColor: "#fff",
                border: "1px solid #E5E7EB",
                boxShadow: "0 6px 16px rgba(15, 23, 42, 0.08)",
                "&:hover": { backgroundColor: "#F9FAFB" },
                display: { xs: "none", md: "inline-flex" },
                zIndex: 1,
              }}
            >
              <ChevronRightRoundedIcon />
            </IconButton>
            <Box
              ref={projectScrollRef}
              sx={{
                display: "flex",
                gap: 2,
                overflowX: "auto",
                pb: 1,
                width: "100%",
                scrollSnapType: "x mandatory",
                scrollBehavior: "smooth",
                "&::-webkit-scrollbar": { display: "none" },
                scrollbarWidth: "none",
                msOverflowStyle: "none",
              }}
            >
            {sortedProjects.map((project, index) => {
              const isSelected =
                Boolean(project._id) && project._id === selectedProjectId;
              const startLabel = project.projectStartDate
                ? `Start ${formatShortDate(project.projectStartDate)}`
                : "Start date not set";
              const endDateValue =
                project.completionConfirmedAt ??
                project.archivedAt ??
                project.deadlineDate ??
                "";
              const endLabel = endDateValue
                ? `End ${formatShortDate(endDateValue)}`
                : "End date not set";
              const budgetLabel = getProjectBudgetLabel(project);
              return (
                <Box
                  key={project._id ?? `${project.projectName ?? "project"}-${index}`}
                  role="button"
                  tabIndex={0}
                  onClick={() => {
                    if (project._id) {
                      setSelectedProjectId(project._id);
                    }
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      if (project._id) {
                        setSelectedProjectId(project._id);
                      }
                    }
                  }}
                  sx={{
                    minWidth: 220,
                    borderRadius: 2,
                    border: isSelected ? "2px solid #4F46E5" : "1px solid #E5E7EB",
                    backgroundColor: isSelected ? "#EEF2FF" : "#fff",
                    boxShadow: isSelected
                      ? "0 10px 24px rgba(79, 70, 229, 0.18)"
                      : "0 6px 16px rgba(15, 23, 42, 0.06)",
                    p: 2,
                    cursor: project._id ? "pointer" : "default",
                    outline: "none",
                    scrollSnapAlign: "start",
                    "&:focus-visible": {
                      boxShadow: "0 0 0 3px rgba(79, 70, 229, 0.3)",
                    },
                  }}
                >
                  <Stack spacing={0.6}>
                    <Typography sx={{ fontWeight: 700, color: "#111827" }}>
                      {project.projectName || "Untitled Project"}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#6B7280" }}>
                      {getProjectScopeLabel(project)}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#9CA3AF" }}>
                      {startLabel}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#9CA3AF" }}>
                      {endLabel}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#6B7280" }}>
                      {`Budget ${budgetLabel}`}
                    </Typography>
                    {project.active ? (
                      <Chip
                        label="Active"
                        size="small"
                        sx={{
                          width: "fit-content",
                          backgroundColor: "#ECFDF3",
                          color: "#047857",
                          fontWeight: 600,
                        }}
                      />
                    ) : null}
                  </Stack>
                </Box>
              );
            })}
            </Box>
          </Box>
        ) : (
          <Typography sx={{ mt: 2, color: "#9CA3AF" }}>
            No projects yet.
          </Typography>
        )}
      </Box>

      <Box sx={{ maxWidth: 628, width: "100%" }}>
        <Typography
          sx={{
            fontWeight: 700,
            color: "#111827",
            fontSize: "1.05rem",
          }}
        >
          Project Wish List
        </Typography>
        {wishlistItems.length ? (
          <Box
            component="ol"
            sx={{
              mt: 2,
              pl: 3,
              color: "#6B7280",
              display: "grid",
              gap: 1,
            }}
          >
            {wishlistItems.map((item, index) => (
              <Typography component="li" key={`${item}-${index}`} sx={{ color: "#6B7280" }}>
                {item}
              </Typography>
            ))}
          </Box>
        ) : (
          <Typography sx={{ mt: 2, color: "#9CA3AF" }}>Not provided</Typography>
        )}
      </Box>

      <Box sx={{ maxWidth: 628, width: "100%" }}>
        <Typography
          sx={{
            fontWeight: 700,
            color: "#111827",
            fontSize: "1.05rem",
          }}
        >
          Additional Notes for Designer, Key Considerations and Goals
        </Typography>
        {projectGoals ? (
          <Box
            sx={{
              mt: 2,
              color: "#6B7280",
              "& p": { margin: 0 },
              "& p + p": { marginTop: 1 },
            }}
            dangerouslySetInnerHTML={{
              __html: projectGoals,
            }}
          />
        ) : (
          <Typography sx={{ mt: 2, color: "#9CA3AF" }}>Not provided</Typography>
        )}
      </Box>
    </Stack>
  );
};

export default NotesTab;
